import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { configureApiClient } from "@teamblob/api-client";

import { routeTree } from "./routeTree.gen.js";
import { QueryProvider } from "./providers/query-provider.js";
import { LocaleProvider } from "./providers/locale-provider.js";
import { QuietRewardsProvider } from "./providers/QuietRewardsContext.js";
import { ErrorBoundary } from "./components/feedback/ErrorBoundary.js";
import { startBidiText } from "./lib/bidi-text.js";

import "./styles/globals.css";

// ---------------------------------------------------------------------------
// Bootstrap order matters
// ---------------------------------------------------------------------------
//
// 1) Configure the API client BEFORE the router renders so any route's
//    `beforeLoad` can call `api.get(...)` (e.g. checkAuth on the protected
//    layout). The Proxy-based singleton in @teamblob/api-client throws a
//    clear error if accessed before configure.
//
// 2) Build the router after step 1 so its first navigation event sees a
//    configured client.
//
// 3) Mount React under <StrictMode> for dev — Vite + React 19 catch
//    double-effect bugs at this layer.
// ---------------------------------------------------------------------------

configureApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "http://localhost:3002",
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// TanStack Router strict types — register so all useLoaderData/etc calls are typed.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Mixed Arabic/English text keeps its word order on every page (see
// lib/bidi-text.ts). Started before the first render so nothing shows
// unsealed.
startBidiText();

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Mount point #root missing from index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary context="App">
      <QueryProvider>
        <LocaleProvider>
          <QuietRewardsProvider>
            <RouterProvider router={router} />
          </QuietRewardsProvider>
        </LocaleProvider>
      </QueryProvider>
    </ErrorBoundary>
  </StrictMode>,
);
